/**
 * Shared primitives for the login flow. Styling matches the design handoff
 * tokens exactly (colours, radii, weights).
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';

/**
 * The IdeaBrief mark: an accent square with three dots — one solid white at the
 * top left, and two muted ones stacked on the right axis.
 *
 * The white dot carries the mark's weight and now sits top left, so the eye
 * enters there and reads down-right across the muted pair. Both right-axis dots
 * share the lighter 55% treatment: the mark reads as one bright accent against
 * a quieter rhythm, rather than as three competing pips. They are white at low
 * opacity rather than `--acc` itself, which would be invisible on an identical
 * background.
 *
 * Every value scales from `size`, so the proportions hold at 18px (dashboard
 * header) through 56px. Do not hard-code pixel offsets here.
 *
 * `inset` is an EDGE inset, applied on both axes, so it bounds the horizontal
 * gap between the two top dots: each dot occupies `inset + dot` from its side,
 * and the pair only clears if `2 * (inset + dot) < size`. The earlier
 * single-column values (dot .27 / inset .21) budget 1.08 of the width and made
 * the top dots overlap at every size except 20px. These ratios match the
 * favicon's proportions, which were drawn by hand and read correctly.
 */
export function Logo({ size = 22 }: { size?: number }) {
  const dot = Math.round(size * 0.25);
  const inset = Math.round(size * 0.17);
  // Gap measured centre-to-centre, then expressed as the second dot's top.
  const secondTop = inset + Math.round(size * 0.38);
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size <= 20 ? 5 : 6,
        background: 'var(--acc)',
        position: 'relative',
        flex: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: inset,
          left: inset,
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: '#fff',
          opacity: 0.9,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: inset,
          right: inset,
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: '#fff',
          opacity: 0.55,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: secondTop,
          right: inset,
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: '#fff',
          opacity: 0.55,
        }}
      />
    </div>
  );
}

export function Wordmark({ size = 16.5 }: { size?: number }) {
  return (
    <span style={{ fontSize: size, fontWeight: 600, letterSpacing: '-.02em' }}>
      IdeaBrief
    </span>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'lg' | 'md' | 'sm';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
}

const SIZES: Record<ButtonSize, { padding: string; fontSize: number }> = {
  lg: { padding: '15px 24px', fontSize: 16 },
  md: { padding: '13px 20px', fontSize: 15 },
  sm: { padding: '9px 14px', fontSize: 13.5 },
};

export function Button({
  variant = 'primary',
  size = 'md',
  full = false,
  style,
  disabled,
  ...rest
}: ButtonProps) {
  const s = SIZES[size];
  const base = {
    padding: s.padding,
    fontSize: s.fontSize,
    borderRadius: size === 'sm' ? 8 : 10,
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '-.01em',
    width: full ? '100%' : undefined,
    // Deliberately not `disabled` in the DOM for gated submits: the design
    // wants a 50%-opacity button that is still clickable so it can surface the
    // reason. Real disabled state is passed explicitly where appropriate.
    opacity: disabled ? 0.5 : 1,
    transition: 'background .18s ease, border-color .18s ease',
  } as const;

  const variants = {
    primary: {
      background: 'var(--acc)',
      color: '#fff',
      border: 'none',
      fontWeight: 600,
      boxShadow: 'var(--shadow-button)',
    },
    secondary: {
      background: 'var(--card)',
      color: 'var(--ink)',
      border: '1px solid var(--line)',
      fontWeight: 500,
    },
    ghost: {
      background: 'none',
      color: 'var(--ink2)',
      border: 'none',
      fontWeight: 500,
      boxShadow: 'none',
    },
  } as const;

  return (
    <button
      {...rest}
      disabled={disabled}
      style={{ ...base, ...variants[variant], ...style }}
    />
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | null;
  hint?: string;
  /**
   * Forwarded to the underlying <input>. Needed because `autoFocus` only fires
   * on mount, and screens that swap a field's purpose in place (sign-up →
   * sign-in) reuse the same element — so focus has to be driven imperatively.
   */
  ref?: Ref<HTMLInputElement>;
}

export function Field({ label, error, hint, id, ...rest }: FieldProps) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${inputId}-error`;

  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={inputId}
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--ink2)',
          marginBottom: 8,
        }}
      >
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        style={{
          width: '100%',
          border: `1px solid ${error ? 'var(--error-line)' : 'var(--line)'}`,
          borderRadius: 10,
          padding: '13px 14px',
          fontSize: 15.5,
          color: 'var(--ink)',
          outline: 'none',
          background: '#fff',
        }}
      />
      {hint && !error && (
        <p style={{ margin: '7px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          style={{
            margin: '9px 0 0',
            fontSize: 13.5,
            color: 'var(--error)',
            display: 'flex',
            gap: 7,
            alignItems: 'flex-start',
          }}
        >
          <span aria-hidden="true" style={{ flex: 'none' }}>
            ✕
          </span>
          {error}
        </p>
      )}
    </div>
  );
}

export function Mono({
  children,
  size = 10.5,
  color = 'var(--muted)',
  style,
}: {
  children: ReactNode;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      style={{
        font: `500 ${size}px/1 var(--font-mono)`,
        color,
        letterSpacing: '.06em',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div
      role="status"
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '2px solid var(--acc-line)',
          borderTopColor: 'var(--acc)',
          animation: 'ibSpin .7s linear infinite',
          flex: 'none',
        }}
      />
      <span style={{ fontSize: 15, color: 'var(--ink2)' }}>{label}</span>
    </div>
  );
}

/**
 * Non-field errors (failed sign-in, rate limiting). Uses role="alert" so
 * screen readers announce it when it appears.
 */
export function FormError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      style={{
        margin: '0 0 14px',
        fontSize: 13.5,
        color: 'var(--error)',
        display: 'flex',
        gap: 7,
        alignItems: 'flex-start',
        lineHeight: 1.5,
      }}
    >
      <span aria-hidden="true" style={{ flex: 'none' }}>
        ✕
      </span>
      {children}
    </p>
  );
}

export function Notice({
  tone = 'accent',
  children,
}: {
  tone?: 'accent' | 'green' | 'amber';
  children: ReactNode;
}) {
  const tones = {
    accent: { bg: 'var(--acc-soft)', line: 'var(--acc-line)', fg: 'var(--ink2)' },
    green: { bg: 'var(--green-soft)', line: 'var(--green-line)', fg: 'var(--ink2)' },
    amber: { bg: 'var(--amber-soft)', line: 'var(--amber-line)', fg: 'var(--ink2)' },
  } as const;
  const t = tones[tone];

  return (
    <div
      style={{
        background: t.bg,
        border: `1px solid ${t.line}`,
        borderRadius: 12,
        padding: '14px 16px',
        fontSize: 14,
        lineHeight: 1.6,
        color: t.fg,
      }}
    >
      {children}
    </div>
  );
}
